import { Factory, Users, Coins, ShieldCheck, ArrowRight, Info } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * CEEFinancingExplainer — Bloc pédagogique institutionnel expliquant
 * comment sont financées les aides CEE. À insérer sur la Home ou /financement-cee.
 */
export default function CEEFinancingExplainer() {
  return (
    <section
      data-testid="cee-financing-explainer"
      className="relative overflow-hidden bg-white border-y border-slate-200 py-20 md:py-24"
    >
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#0055A4]/25 bg-[#0055A4]/[0.06] px-3 py-1.5 mb-5">
            <Info className="h-3.5 w-3.5 text-[#0055A4]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#0055A4] font-bold">
              Comprendre le dispositif
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05] mb-5">
            Comment sont financées les aides CEE&nbsp;?
          </h2>
          <p className="text-slate-600 text-base md:text-lg leading-relaxed">
            Les Certificats d&apos;Économies d&apos;Énergie ne coûtent rien à l&apos;État.
            Ils reposent sur un mécanisme intelligent instauré par la loi POPE de 2005 :
            <strong className="text-slate-900"> les grands énergéticiens financent la rénovation des logements français.</strong>
          </p>
        </div>

        {/* Flow diagram */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-4 items-stretch mb-12">
          {/* Step 1 — Obligés */}
          <FlowStep
            icon={Factory}
            step="01"
            title="Les énergéticiens obligés"
            desc="EDF, TotalEnergies, Engie, Esso… sont légalement tenus par l'État de financer des économies d'énergie chez leurs clients. Chaque année, des objectifs chiffrés (en kWh cumac économisés) leur sont fixés."
            accent="#0055A4"
          />
          {/* Step 2 — Delegataires */}
          <FlowStep
            icon={ShieldCheck}
            step="02"
            title="Les délégataires agréés"
            desc="ECONATION-CEE agit en tant que mandataire agréé. Nous collectons les preuves de travaux, montons les dossiers CEE conformes et les vendons aux obligés qui ont besoin de certificats pour remplir leurs obligations."
            accent="#10B981"
            highlight
          />
          {/* Step 3 — Bénéficiaires */}
          <FlowStep
            icon={Users}
            step="03"
            title="Vous, bénéficiaire final"
            desc="Vous recevez le financement CEE sous forme de prime ou de réduction directe sur vos travaux de rénovation. Aucune avance de trésorerie n'est nécessaire dans la plupart des cas."
            accent="#0055A4"
          />
        </div>

        {/* Key facts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { value: "0€", label: "Coût pour l'État", color: "text-[#0055A4]" },
            { value: "2005", label: "Loi POPE fondatrice", color: "text-slate-900" },
            { value: "20 Md€", label: "Investis en 20 ans", color: "text-[#10B981]" },
            { value: "100%", label: "Traçable & audité", color: "text-[#0055A4]" },
          ].map((k) => (
            <div key={k.label} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className={`font-display text-3xl sm:text-4xl font-bold tracking-tight ${k.color}`}>{k.value}</div>
              <div className="text-slate-500 text-xs mt-1.5 uppercase tracking-wide font-semibold">{k.label}</div>
            </div>
          ))}
        </div>

        {/* Sources / trust */}
        <div className="rounded-2xl bg-slate-50 border border-slate-200 px-5 py-4 mb-8">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-[#10B981] mt-0.5 flex-shrink-0" />
            <div className="text-sm text-slate-700 leading-relaxed">
              <strong className="text-slate-900">Source officielle :</strong> Article L221-1 et suivants du Code de l&apos;énergie ·
              Décret n° 2010-1663 du 29 décembre 2010 · Dispositif piloté par le
              <strong className="text-slate-900"> Ministère de la Transition écologique</strong> et contrôlé par le Pôle National des CEE.
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            to="/financement-cee"
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 text-sm font-semibold tracking-wide transition"
          >
            En savoir plus sur le dispositif CEE <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/simulateur-pac"
            className="inline-flex items-center gap-2 rounded-full border-2 border-slate-300 hover:border-slate-500 text-slate-900 px-6 py-3 text-sm font-semibold tracking-wide transition"
          >
            <Coins className="h-4 w-4" /> Estimer mes aides CEE
          </Link>
        </div>
      </div>
    </section>
  );
}

function FlowStep({ icon: Ic, step, title, desc, accent, highlight }) {
  return (
    <div
      className={`relative rounded-2xl border-2 p-6 md:p-7 h-full transition ${
        highlight
          ? "border-transparent bg-slate-900 text-white shadow-xl"
          : "border-slate-200 bg-white text-slate-900 hover:border-slate-300"
      }`}
    >
      <div className="flex items-center justify-between mb-5">
        <div
          className="h-11 w-11 rounded-xl grid place-items-center flex-shrink-0"
          style={{ backgroundColor: `${accent}20`, color: accent }}
        >
          <Ic className="h-5 w-5" />
        </div>
        <div
          className={`text-[11px] font-bold tracking-[0.24em] ${highlight ? "text-white/40" : "text-slate-400"}`}
        >
          ÉTAPE {step}
        </div>
      </div>
      <div className={`font-display text-lg font-bold tracking-tight mb-2 ${highlight ? "text-white" : "text-slate-900"}`}>
        {title}
      </div>
      <p className={`text-sm leading-relaxed ${highlight ? "text-white/75" : "text-slate-600"}`}>{desc}</p>
    </div>
  );
}
